import {
  type ComponentPropsWithRef,
  type ElementRef,
  type ReactNode,
  forwardRef
} from 'react'
import { FaDownload, FaUpload } from 'react-icons/fa'

import { ACTIONS } from '@/constants/common/message.constants'

import { type Button } from '../Button'
import { IconTextButton } from '../IconTextButton'

type CsvAction = 'export' | 'import'

type Props = Omit<ComponentPropsWithRef<typeof Button>, 'children' | 'icon'> & {
  /**
   * 操作種別。CSV データの書き出し=export / 取り込み=import（#4198 の用語辞書）。
   * アイコンは export=FaDownload(下矢印) / import=FaUpload(上矢印) に対応する。
   */
  action: CsvAction
  /** ボタンラベル。未指定時は action に応じた既定ラベルを表示する */
  label?: ReactNode
  /** アイコンに付与する className。既定は `opacity-80`（CSV ヘッダボタンの慣習） */
  iconClassName?: string
}

const DEFAULT_ICON_CLASS_NAME = 'opacity-80'

/**
 * action 別の既定ラベル。CSV 操作ボタンのラベルは「そのボタンが何であるか」に固有＝
 * 本コンポーネントが既定として持つ（呼び出し側は label を渡さない）。
 * 文字列は集約規約（no-restricted-syntax）に従い `ACTIONS` 定数を参照する。
 */
const DEFAULT_LABEL: Record<CsvAction, string> = {
  export: ACTIONS.EXPORT_CSV,
  import: ACTIONS.IMPORT_CSV
}

/**
 * CSV 操作（エクスポート/インポート）ボタン。
 *
 * 汎用の `IconTextButton` をラップし、CSV の慣習（FaDownload/FaUpload アイコン・
 * `opacity-80` の淡色スタイル）を既定として付与する。アイコンの淡色は `iconClassName`
 * で上書き可能。CSV エクスポート（各マスタ/一覧）と CSV インポート（#4195）で共用する。
 */
export const CsvActionButton = forwardRef<ElementRef<typeof Button>, Props>(
  (
    { action, label, iconClassName = DEFAULT_ICON_CLASS_NAME, ...props },
    ref
  ) => {
    const icon =
      action === 'export' ? (
        <FaDownload className={iconClassName} />
      ) : (
        <FaUpload className={iconClassName} />
      )

    return (
      <IconTextButton
        ref={ref}
        icon={icon}
        label={label ?? DEFAULT_LABEL[action]}
        {...props}
      />
    )
  }
)
CsvActionButton.displayName = 'CsvActionButton'
